import { InputAdornment, InputBaseProps } from '@mui/material';
import { forwardRef } from 'react';

import { IconCloseMS, IconSearchMS } from '../../../constants/icons';
import { Constants } from '../../../constants/searchField';
import { ClearButton, Root } from './styles';

export interface SearchFieldProps
  extends Omit<
    InputBaseProps,
    'value' | 'onChange' | 'startAdornment' | 'type' | 'multiline'
  > {
  value: string;
  onChange: (value: string) => void;
  /** Runs instead of `onChange('')` when the clear button is pressed. */
  onClear?: () => void;
  maxLength?: number;
}

/**
 * Search input of the v2 design system: 40px, search icon at the start and a
 * clear button while there is text. Without `fullWidth` the input grows on
 * focus, for toolbars; with it, it fills the container, for panels.
 */
export const SearchField = forwardRef<HTMLDivElement, SearchFieldProps>(
  function SearchField(
    {
      value,
      onChange,
      onClear,
      maxLength,
      placeholder,
      disabled,
      endAdornment,
      inputProps,
      ...props
    },
    ref
  ): JSX.Element {
    const handleClear = (): void => {
      if (onClear) onClear();
      else onChange('');
    };

    return (
      <Root
        {...props}
        ref={ref}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ 'aria-label': placeholder, maxLength, ...inputProps }}
        startAdornment={
          <InputAdornment position="start">{IconSearchMS}</InputAdornment>
        }
        endAdornment={
          (value.length > 0 && !disabled) || endAdornment ? (
            <InputAdornment position="end">
              {value.length > 0 && !disabled && (
                <ClearButton
                  aria-label={Constants.clearSearch}
                  onClick={handleClear}
                >
                  {IconCloseMS}
                </ClearButton>
              )}
              {endAdornment}
            </InputAdornment>
          ) : undefined
        }
      />
    );
  }
);
